import { useEffect, useMemo, useState } from 'react';
import templateProduct from '../data/templateProduct';
import { createOrder, fetchProduct, updateUserAddress } from '../services/api';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';

const PICKUP_ADDRESS = '40 Rue Championnet 75018 Paris';
const PAYMENT_PAGE = '/paiements';
const DEFAULT_SHIPPING_FEE_FRANCE = 5;
const DEFAULT_SHIPPING_FEE_INTL = 12;

const resolveShippingFee = (countryValue) => {
  const normalized = (countryValue || '').toLowerCase().trim();
  if (!normalized) return DEFAULT_SHIPPING_FEE_FRANCE;
  const isFrance = normalized === 'france' || normalized === 'fr' || normalized.includes('france');
  return isFrance ? DEFAULT_SHIPPING_FEE_FRANCE : DEFAULT_SHIPPING_FEE_INTL;
};

export default function CartDrawer({ isOpen, onClose }) {
  const { items, updateQuantity, removeItem, clearCart } = useCart();
  const { user, refreshUser } = useAuth();
  const [status, setStatus] = useState('');
  const [shippingAddress, setShippingAddress] = useState({
    street: '',
    postalCode: '',
    city: '',
    country: ''
  });
  const [addressForm, setAddressForm] = useState({
    street: '',
    postalCode: '',
    city: '',
    country: ''
  });
  const [editingAddress, setEditingAddress] = useState(false);
  const [shippingMethod, setShippingMethod] = useState('pickup');
  const hasItems = items.length > 0;
  const subtotal = useMemo(() => items.reduce((acc, item) => acc + item.price * item.quantity, 0), [items]);
  const [shippingFee, setShippingFee] = useState(0);
  const [visualCache, setVisualCache] = useState({});
  const discountPercent = useMemo(() => {
    const value = Number(user?.discountPercent) || 0;
    if (value < 0) return 0;
    if (value > 100) return 100;
    return value;
  }, [user?.discountPercent]);
  const discountAmount = useMemo(() => {
    if (!discountPercent) return 0;
    return Number(((subtotal * discountPercent) / 100).toFixed(2));
  }, [subtotal, discountPercent]);
  const discountedSubtotal = useMemo(() => {
    const raw = subtotal - discountAmount;
    return Number(Math.max(0, raw).toFixed(2));
  }, [subtotal, discountAmount]);
  const totalWithShipping = useMemo(() => {
    const fee = shippingMethod === 'delivery' ? Number(shippingFee) || 0 : 0;
    return Number((discountedSubtotal + fee).toFixed(2));
  }, [discountedSubtotal, shippingMethod, shippingFee]);
  const customerName = (user?.firstName || user?.lastName) ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '';
  const customerPhone = user?.phone || user?.mobile || '';

  useEffect(() => {
    if (!user) {
      setShippingAddress({ street: '', postalCode: '', city: '', country: '' });
      setAddressForm({ street: '', postalCode: '', city: '', country: '' });
      setEditingAddress(false);
      setShippingFee(0);
      return;
    }
    const initial = {
      street: user.street || '',
      postalCode: user.postalCode || '',
      city: user.city || '',
      country: user.country || ''
    };
    setShippingAddress(initial);
    setAddressForm(initial);
    setShippingMethod('pickup');
    setShippingFee(0);
  }, [user]);

  useEffect(() => {
    const missing = items
      .filter((item) => item.productId && !item.visual && !visualCache[item.productId])
      .map((item) => item.productId);
    if (!missing.length) return;
    missing.forEach((productId) => {
      fetchProduct(productId)
        .then((product) => {
          const visual = product?.visuals?.[0] || product?.image || templateProduct.image;
          setVisualCache((prev) => ({ ...prev, [productId]: visual }));
        })
        .catch(() => {
          setVisualCache((prev) => ({ ...prev, [productId]: templateProduct.image }));
        });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  useEffect(() => {
    if (shippingMethod !== 'delivery') {
      setShippingFee(0);
      return;
    }
    const country = shippingAddress.country || addressForm.country || '';
    setShippingFee(resolveShippingFee(country));
  }, [shippingMethod, shippingAddress.country, addressForm.country]);

  const handleCheckout = async () => {
    setStatus('');
    if (!items.length) {
      setStatus('Votre panier est vide.');
      return;
    }
    if (!user?.email) {
      setStatus('Veuillez vous connecter pour valider votre commande.');
      return;
    }
    if (shippingMethod === 'delivery') {
      if (!shippingAddress.street || !shippingAddress.city || !shippingAddress.country) {
        setStatus('Veuillez renseigner votre adresse de livraison.');
        setEditingAddress(true);
        return;
      }
    }
    try {
      const selectedAddress = shippingMethod === 'delivery'
        ? shippingAddress
        : { street: '', postalCode: '', city: '', country: '' };
      const shippingDetails =
        shippingMethod === 'delivery'
          ? {
              provider: 'Livraison à domicile',
              fee: shippingFee,
              estimatedDelivery: '3-5 jours ouvrés'
            }
          : {
              provider: 'Retrait Paris',
              fee: 0,
              estimatedDelivery: `Retrait sur place — ${PICKUP_ADDRESS}`
            };
      await createOrder({
        items: items.map((item) => ({
          productId: item.productId,
          quantity: item.quantity,
          size: item.size
        })),
        customer: {
          email: user.email,
          name: customerName || undefined,
          phone: customerPhone || undefined,
          street: selectedAddress.street,
          postalCode: selectedAddress.postalCode,
          city: selectedAddress.city,
          country: selectedAddress.country
        },
        shipping: shippingDetails
      });
      setStatus('Commande enregistrée ! Un e-mail de confirmation vous sera envoyé.');
      clearCart();
      if (typeof window !== 'undefined') {
        window.open(PAYMENT_PAGE, '_blank');
      }
    } catch (err) {
      setStatus(err.message);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="cart-drawer" role="dialog" aria-modal="true" aria-label="Panier" onClick={onClose}>
      <div className="cart-drawer__panel" onClick={(event) => event.stopPropagation()}>
        <header className="cart-drawer__header">
          <h3>Panier</h3>
          <button type="button" onClick={onClose} aria-label="Fermer">
            ×
          </button>
        </header>
        {hasItems ? (
          <ul className="cart-drawer__list">
            {items.map((item) => {
              const resolvedVisual = item.visual || visualCache[item.productId];
              return (
                <li key={item.key} className="cart-drawer__item">
                  {resolvedVisual && <img src={resolvedVisual} alt={item.name} />}
                <div className="cart-drawer__details">
                  <p>{item.name}</p>
                  <small>Taille : {item.size}</small>
                  <p className="cart-drawer__price">
                    {(item.price * item.quantity).toFixed(2)} € ({item.price.toFixed(2)} €)
                  </p>
                </div>
                <div className="cart-drawer__qty">
                  <button
                    type="button"
                    onClick={() => {
                      setStatus('');
                      updateQuantity(item.key, -1);
                    }}
                  >
                    –
                  </button>
                  <span>{item.quantity}</span>
                  <button
                    type="button"
                    onClick={() => {
                      setStatus('');
                      updateQuantity(item.key, 1);
                    }}
                  >
                    +
                  </button>
                </div>
                <button
                  type="button"
                  className="cart-drawer__remove"
                  onClick={() => {
                    setStatus('');
                    removeItem(item.key);
                  }}
                  aria-label="Supprimer"
                >
                  Supprimer
                </button>
              </li>
            );
            })}
          </ul>
        ) : (
          <p className="cart-drawer__empty">Votre panier est vide.</p>
        )}
        <div className="cart-drawer__delivery-options">
          <label>
            <input
              type="radio"
              name="shipping-method"
              value="pickup"
              checked={shippingMethod === 'pickup'}
              onChange={() => {
                setShippingMethod('pickup');
                setEditingAddress(false);
              }}
            />
            Retirer votre commande à{' '}
            <a
              href="https://www.google.com/maps/dir/?api=1&destination=40+Rue+Championnet+75018+Paris"
              target="_blank"
              rel="noopener noreferrer"
              className="cart-drawer__pickup-link"
            >
              Paris
            </a>
          </label>
          <label>
            <input
              type="radio"
              name="shipping-method"
              value="delivery"
              checked={shippingMethod === 'delivery'}
              onChange={() => {
              setShippingMethod('delivery');
                const countryValue = shippingAddress.country || addressForm.country || '';
                setShippingFee(resolveShippingFee(countryValue));
                if (!shippingAddress.street || !shippingAddress.city || !shippingAddress.country) {
                  setEditingAddress(true);
                }
              }}
            />
            Livraison à domicile
          </label>
        </div>
        {user?.email && shippingMethod === 'delivery' && (
          <div className="cart-drawer__shipping">
            <div className="cart-drawer__shipping-header">
              <h4>Adresse de livraison</h4>
              {!editingAddress && (
                <button type="button" className="cart-drawer__change-address" onClick={() => setEditingAddress(true)}>
                  Changer d’adresse
                </button>
              )}
            </div>
            {!editingAddress ? (
              <p className="cart-drawer__shipping-summary">
                {shippingAddress.street ? (
                  <>
                    {shippingAddress.street}
                    <br />
                    {shippingAddress.postalCode} {shippingAddress.city}
                    <br />
                    {shippingAddress.country}
                  </>
                ) : (
                  'Adresse non renseignée.'
                )}
              </p>
            ) : (
              <form
                className="cart-drawer__shipping-form"
                onSubmit={async (event) => {
                  event.preventDefault();
                  setShippingAddress(addressForm);
                  setEditingAddress(false);
                  if (user?._id) {
                    try {
                      await updateUserAddress(user._id, addressForm);
                      await refreshUser();
                    } catch (error) {
                      setStatus(error.message);
                    }
                  }
                }}
              >
                <label>
                  Nom et numéro de la voie
                  <input
                    type="text"
                    value={addressForm.street}
                    onChange={(event) => setAddressForm((prev) => ({ ...prev, street: event.target.value }))}
                    required
                  />
                </label>
                <label>
                  Code postal
                  <input
                    type="text"
                    value={addressForm.postalCode}
                    onChange={(event) => setAddressForm((prev) => ({ ...prev, postalCode: event.target.value }))}
                    required
                  />
                </label>
                <label>
                  Ville
                  <input
                    type="text"
                    value={addressForm.city}
                    onChange={(event) => setAddressForm((prev) => ({ ...prev, city: event.target.value }))}
                    required
                  />
                </label>
                <label>
                  Pays
                  <input
                    type="text"
                    value={addressForm.country}
                    onChange={(event) => setAddressForm((prev) => ({ ...prev, country: event.target.value }))}
                    required
                  />
                </label>
                <div className="cart-drawer__shipping-actions">
                  <button type="submit" className="product-detail__primary">
                    Enregistrer
                  </button>
                  <button
                    type="button"
                    className="site-header__cta site-header__cta--ghost"
                    onClick={() => {
                      setAddressForm(shippingAddress);
                      setEditingAddress(false);
                    }}
                  >
                    Annuler
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
        <footer className="cart-drawer__footer">
          {discountPercent > 0 && (
            <p className="cart-drawer__discount-line">
              Réduction <strong>-{discountAmount.toFixed(2)} €</strong>
            </p>
          )}
          <p>
            Sous-total <strong>{subtotal.toFixed(2)} €</strong>
          </p>
          {shippingMethod === 'delivery' && (
            <p>
              Frais de livraison{' '}
              <strong>{shippingFee.toFixed(2)} €</strong>
            </p>
          )}
          <p>
            Total
            <strong>{totalWithShipping.toFixed(2)} €</strong>
          </p>
          <button type="button" className="product-detail__primary" disabled={!hasItems} onClick={handleCheckout}>
            Valider la commande
          </button>
        </footer>
        {status && <p className="cart-drawer__status">{status}</p>}
      </div>
    </div>
  );
}
